/**
 * RBA Package Protection
 * ----------------------
 * Keeps a hidden "RBA Package Protection" line item in sync with the cart.
 *
 * How the fee is charged
 *   Shopify prices a line item by its variant, so a theme cannot push an
 *   arbitrary amount into checkout. There are two ways round that, and which
 *   one runs depends on whether a price ladder is configured.
 *
 *   Ladder mode (preferred). The protection product carries a list of fixed
 *   price points. We add quantity 1 of the cheapest point that covers the fee,
 *   so checkout reads "RBA Package Protection - $8.00" with no odd quantity.
 *   No app and no Shopify Plus: the variant genuinely costs that much, so
 *   nothing is being overridden.
 *
 *   Legacy mode (fallback, used when no ladder is set). The product is priced
 *   at $0.01 and the quantity carries the fee: CA$7.43 becomes quantity 743.
 *   It charges correctly but shows that quantity at checkout, which is what
 *   the ladder exists to fix.
 *
 * Where the numbers come from
 *   Liquid does the maths server-side on every render and hands the result down
 *   as data attributes. This file only reconciles the cart with that result, so
 *   the displayed fee and the charged fee cannot drift apart.
 *
 * Settings live in snippets/rba-protection-config.liquid.
 */

import { sectionRenderer } from '@theme/section-renderer';

/** Cart attribute used to remember a deliberate opt-out across refreshes. */
const OPT_OUT_ATTRIBUTE = 'rba_protection';

/** Line item property that marks our line, so other code can spot it without the variant ID. */
const LINE_MARKER = '_rba_protection';

/**
 * Guards against a runaway add/recalculate loop. A single cart change should
 * settle in one correction; anything beyond this means something is fighting us
 * and we stop rather than hammer the cart API.
 */
const MAX_CORRECTIONS_PER_BURST = 3;

/**
 * The cart drawer is rendered on every page (layout/theme.liquid), so on /cart
 * there are two widgets on screen at once. Cart writes are therefore serialised
 * across every instance: each reconcile re-reads the cart before acting, so once
 * one instance has corrected the line the next finds nothing to do.
 * @type {Promise<unknown>}
 */
let cartWriteQueue = Promise.resolve();

/** True while any instance is mid-write, so we can ignore the echo of our own change. */
let writeInFlight = false;

/**
 * Runs `task` after any in-flight cart write finishes.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T | undefined>}
 */
const queueCartWrite = (task) => {
  const run = cartWriteQueue.then(
    () => task(),
    () => task()
  );
  cartWriteQueue = run.catch(() => {});
  return run;
};

/** @param {Response} response */
const asJson = (response) => {
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
};

const postCart = (path, body) =>
  fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then(asJson);

/**
 * Turns the configured "cents:variantId,..." string into a sorted list.
 * Anything malformed is dropped rather than throwing, because a broken ladder
 * must degrade to legacy mode, not break the cart.
 *
 * @param {string | undefined} raw
 * @returns {Array<{cents: number, variantId: number}>} ascending by price
 */
const parseLadder = (raw) => {
  if (!raw) return [];

  return raw
    .split(',')
    .map((entry) => {
      const [cents, variantId] = entry.split(':').map((part) => Number(part.trim()));
      return { cents, variantId };
    })
    .filter((step) => Number.isFinite(step.cents) && step.cents > 0 && Number.isFinite(step.variantId) && step.variantId > 0)
    .sort((a, b) => a.cents - b.cents);
};

class RbaPackageProtection extends HTMLElement {
  #corrections = 0;
  #busy = false;
  #abortResetTimer = null;

  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.ladder = parseLadder(this.dataset.ladder);
    this.ladderVariantIds = new Set(this.ladder.map((step) => step.variantId));
    this.percent = Number(this.dataset.percent);
    this.minimumCents = Number(this.dataset.minimumCents);
    this.defaultOn = this.dataset.defaultOn === 'true';
    this.sectionId = this.dataset.sectionId;
    this.isDrawer = this.dataset.context === 'drawer';

    this.toggle = this.querySelector('[ref="toggle"]');
    this.warning = this.querySelector('[ref="warning"]');
    this.infoButton = this.querySelector('[ref="infoButton"]');
    this.modal = this.querySelector('[ref="modal"]');
    this.modalClose = this.querySelector('[ref="modalClose"]');
    this.modalDismiss = this.querySelector('[ref="modalDismiss"]');

    this.toggle?.addEventListener('change', this.#onToggle);
    this.infoButton?.addEventListener('click', this.#openModal);
    this.modalClose?.addEventListener('click', this.#closeModal);
    this.modalDismiss?.addEventListener('click', this.#closeModal);
    this.modal?.addEventListener('click', this.#onModalBackdropClick);

    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);

    // Server-rendered state is authoritative. Only touch the cart when it
    // disagrees with what Liquid just calculated, so the common case — a cart
    // that is already correct — costs nothing.
    if (this.#needsReconcile()) this.#reconcile();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.#onToggle);
    this.infoButton?.removeEventListener('click', this.#openModal);
    this.modalClose?.removeEventListener('click', this.#closeModal);
    this.modalDismiss?.removeEventListener('click', this.#closeModal);
    this.modal?.removeEventListener('click', this.#onModalBackdropClick);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    clearTimeout(this.#abortResetTimer);
  }

  /** True when what is in the cart does not match what Liquid rendered. */
  #needsReconcile() {
    const isOn = this.dataset.state === 'on';
    const target = Number(this.dataset.targetCents);
    const current = Number(this.dataset.currentCents);

    if (!isOn) return current !== 0;
    if (current !== target) return true;

    // Right amount, but sitting on the wrong price point — happens when a cart
    // predates the ladder, or the ladder was regenerated.
    const targetVariant = Number(this.dataset.targetVariantId);
    const currentVariant = Number(this.dataset.currentVariantId);
    return targetVariant > 0 && currentVariant > 0 && targetVariant !== currentVariant;
  }

  /** A cart change happened somewhere in the theme — re-check our line. */
  #onCartUpdate = (event) => {
    // Ignore the echo of our own mutation, from this instance or the other one.
    if (this.#busy || writeInFlight) return;
    // A burst of changes (e.g. quantity spinner) shares one correction budget.
    clearTimeout(this.#abortResetTimer);
    this.#abortResetTimer = setTimeout(() => {
      this.#corrections = 0;
    }, 1500);

    const promise = event?.promise;
    if (promise?.then) {
      promise.then(() => this.#reconcile()).catch(() => {});
    } else {
      this.#reconcile();
    }
  };

  /** @param {number} goodsSubtotal - subtotal in cents, excluding protection */
  #targetFor(goodsSubtotal) {
    if (goodsSubtotal <= 0) return 0;
    return Math.max(Math.round((goodsSubtotal * this.percent) / 100), this.minimumCents);
  }

  /**
   * What the protection line should look like for a given cart.
   *
   * Mirrors the snapping the Liquid does on every render, so the fee shown and
   * the fee charged cannot drift apart. Returns null when there should be no
   * protection line at all.
   *
   * @param {number} goodsSubtotal - subtotal in cents, excluding protection
   * @returns {{variantId: number, quantity: number, cents: number} | null}
   */
  #desiredLine(goodsSubtotal) {
    const fee = this.#targetFor(goodsSubtotal);
    if (fee <= 0) return null;

    if (this.ladder.length === 0) {
      // Legacy: one $0.01 unit per cent of fee.
      return { variantId: this.variantId, quantity: fee, cents: fee };
    }

    // Cheapest point that covers the fee; the top point caps it.
    const step = this.ladder.find((candidate) => candidate.cents >= fee) ?? this.ladder[this.ladder.length - 1];
    return { variantId: step.variantId, quantity: 1, cents: step.cents };
  }

  /** @param {object} item - a line item from /cart.js */
  #isProtectionLine(item) {
    if (item?.properties?.[LINE_MARKER]) return true;
    if (item?.variant_id === this.variantId) return true;
    return this.ladderVariantIds.has(item?.variant_id);
  }

  /**
   * Move the protection line onto `desired`.
   *
   * A cart line cannot change variant in place, so a move to a different price
   * point is a removal followed by an add. Removing first means the cart is
   * briefly unprotected rather than briefly charged twice.
   *
   * @param {object | null} line - the existing protection line, if any
   * @param {{variantId: number, quantity: number} | null} desired
   * @returns {Promise<boolean>} whether the cart was changed
   */
  async #applyLine(line, desired) {
    if (!desired) {
      if (!line) return false;
      await postCart('/cart/change.js', { id: line.key, quantity: 0 });
      return true;
    }

    if (line && line.variant_id !== desired.variantId) {
      await postCart('/cart/change.js', { id: line.key, quantity: 0 });
      line = null;
    }

    if (!line) {
      await postCart('/cart/add.js', {
        items: [{ id: desired.variantId, quantity: desired.quantity, properties: { [LINE_MARKER]: '1' } }],
      });
      return true;
    }

    if (line.quantity !== desired.quantity) {
      await postCart('/cart/change.js', { id: line.key, quantity: desired.quantity });
      return true;
    }

    return false;
  }

  /**
   * Bring the cart in line with the configured fee.
   * Reads the cart, works out what the protection line should be, and issues at
   * most one mutation. Returns silently when nothing needs doing.
   */
  #reconcile() {
    if (this.#busy) return Promise.resolve();
    if (this.#corrections >= MAX_CORRECTIONS_PER_BURST) {
      console.warn('[rba-protection] correction limit reached, leaving the cart alone');
      return Promise.resolve();
    }

    this.#busy = true;
    this.dataset.busy = 'true';

    return queueCartWrite(() => this.#runReconcile());
  }

  async #runReconcile() {
    try {
      writeInFlight = true;
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(asJson);

      let protectionLine = null;
      let goodsSubtotal = 0;

      for (const item of cart.items) {
        if (this.#isProtectionLine(item)) protectionLine = item;
        else goodsSubtotal += item.final_line_price;
      }

      const optedOut = cart.attributes?.[OPT_OUT_ATTRIBUTE] === 'off';
      const shouldBeOn = !optedOut && goodsSubtotal > 0 && (this.defaultOn || protectionLine != null);
      const desired = shouldBeOn ? this.#desiredLine(goodsSubtotal) : null;

      const changed = await this.#applyLine(protectionLine, desired);

      if (changed) {
        this.#corrections += 1;
        await this.#refreshCartUI();
      }
    } catch (error) {
      console.warn('[rba-protection] could not sync the protection line:', error);
    } finally {
      writeInFlight = false;
      this.#busy = false;
      delete this.dataset.busy;
    }
  }

  /** Customer ticked or unticked the box. */
  #onToggle = async (event) => {
    const wantsProtection = Boolean(event.target.checked);

    // Reflect the choice immediately; the cart catches up a moment later.
    this.dataset.state = wantsProtection ? 'on' : 'off';
    if (this.warning) this.warning.hidden = wantsProtection;

    // Lock the box until the cart agrees, so a double-click cannot leave the
    // checkbox saying one thing and the cart holding another.
    if (this.toggle) this.toggle.disabled = true;
    this.#busy = true;
    this.dataset.busy = 'true';

    await queueCartWrite(() => this.#applyChoice(wantsProtection));
  };

  /** @param {boolean} wantsProtection */
  async #applyChoice(wantsProtection) {
    try {
      writeInFlight = true;

      // Record the choice on the cart so it survives a refresh.
      await postCart('/cart/update.js', {
        attributes: { [OPT_OUT_ATTRIBUTE]: wantsProtection ? 'on' : 'off' },
      });

      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(asJson);

      let protectionLine = null;
      let goodsSubtotal = 0;
      for (const item of cart.items) {
        if (this.#isProtectionLine(item)) protectionLine = item;
        else goodsSubtotal += item.final_line_price;
      }

      if (wantsProtection) {
        await this.#applyLine(protectionLine, this.#desiredLine(goodsSubtotal));
      } else {
        await this.#applyLine(protectionLine, null);
      }

      this.#corrections = 0;
      await this.#refreshCartUI();
    } catch (error) {
      console.warn('[rba-protection] could not apply the protection choice:', error);
      // Put the checkbox back where it was so the UI never lies about the cart.
      if (this.toggle) this.toggle.checked = !wantsProtection;
      this.dataset.state = wantsProtection ? 'off' : 'on';
      if (this.warning) this.warning.hidden = !wantsProtection;
    } finally {
      writeInFlight = false;
      this.#busy = false;
      delete this.dataset.busy;
      if (this.toggle) this.toggle.disabled = false;
    }
  }

  /** Re-render the cart section so totals, the fee and the line list all agree. */
  async #refreshCartUI() {
    if (!this.sectionId) return;

    // On /cart the drawer is in the layout too, so refresh both or the one the
    // customer is not looking at keeps showing the old fee.
    const targets = new Set([this.sectionId]);
    if (document.querySelector('#shopify-section-cart-drawer-section')) {
      targets.add('cart-drawer-section');
    }

    for (const sectionId of targets) {
      try {
        await sectionRenderer.renderSection(sectionId, {
          cache: false,
          mode: sectionId === 'cart-drawer-section' ? 'hydration' : 'full',
        });
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn(`[rba-protection] re-render of "${sectionId}" failed:`, error);
        }
      }
    }
  }

  #openModal = () => {
    if (typeof this.modal?.showModal === 'function') this.modal.showModal();
  };

  #closeModal = () => {
    if (typeof this.modal?.close === 'function') this.modal.close();
  };

  /** Clicking the dark area outside the card closes the dialog. */
  #onModalBackdropClick = (event) => {
    if (event.target === this.modal) this.#closeModal();
  };
}

if (!customElements.get('rba-package-protection')) {
  customElements.define('rba-package-protection', RbaPackageProtection);
}
